// Dead letter bulk actions (QA scenario 1.6.20)
//
// PAGES.md §3.75 specifies "Retry All" / "Discard All" header buttons alongside
// the per-row actions, and lists only the per-id endpoints as the data source —
// there is no bulk endpoint. The buttons therefore fan out over the messages the
// page is currently showing (current page + exchange filter), calling
// POST /dead-letters/:id/retry or DELETE /dead-letters/:id once per message.
//
// Only PENDING rows are actionable: the API answers 409 for a message that has
// already been replayed or discarded, so those are excluded up front.

import { isDeadLetterOpen } from './dead-letter-status';

export type BulkAction = 'retry' | 'discard';

type Actionable = Parameters<typeof isDeadLetterOpen>[0];

/** The listed messages a bulk action applies to — the still-failed ones. */
export function selectBulkTargets<T extends Actionable>(letters: T[]): T[] {
  return letters.filter(isDeadLetterOpen);
}

export interface BulkConfirmCopy {
  title: string;
  description: string;
  confirmLabel: string;
  variant: 'danger' | 'warning';
}

/** Confirmation copy — states the exact count and that the scope is what is listed. */
export function bulkConfirmCopy(action: BulkAction, count: number): BulkConfirmCopy {
  const messages = count === 1 ? '1 failed message' : `${count} failed messages`;
  if (action === 'retry') {
    return {
      title: `Retry ${messages}`,
      description: `${messages} currently listed (this page, current exchange filter) will be republished to the original exchange. Messages already replayed or discarded are skipped.`,
      confirmLabel: 'Retry All',
      variant: 'warning',
    };
  }
  return {
    title: `Discard ${messages}`,
    description: `${messages} currently listed (this page, current exchange filter) will be marked Discarded and can no longer be retried. This cannot be undone.`,
    confirmLabel: 'Discard All',
    variant: 'danger',
  };
}

export interface BulkSummary {
  title: string;
  variant: 'success' | 'warning' | 'error';
}

/** Result toast — a partial run reports both halves rather than claiming success. */
export function summariseBulkRun(action: BulkAction, succeeded: number, failed: number): BulkSummary {
  const verb = action === 'retry' ? 'replayed' : 'discarded';
  const total = succeeded + failed;
  if (succeeded === 0) {
    return { title: `No messages ${verb} — all ${total} failed`, variant: 'error' };
  }
  if (failed > 0) {
    return { title: `${succeeded} of ${total} messages ${verb} — ${failed} failed`, variant: 'warning' };
  }
  const messages = succeeded === 1 ? '1 message' : `${succeeded} messages`;
  return { title: `${messages} ${verb}`, variant: 'success' };
}
